import angular from 'angular';
import { hasOwnProperty } from '../../tn.common';
import { EMPTY_FONT_RIGHTS } from '../../tn.licenses/font-rights';
import { PopoverService } from '../../tn.popover/service.tn.popover';
import { BucketService } from '../../tn.pricing/buckets.service/buckets';
import { CartService } from '../service.tn.cart';
import editStylesTemplate from './edit-styles.html';

export function populateEditStylesTemplateCache($templateCache) {
    $templateCache.put('tn.cart/edit-styles.html', editStylesTemplate);
}
populateEditStylesTemplateCache.$inject = ['$templateCache'];

export function EditStylesController(
    $scope,
    $filter,
    $q,
    bucketsService: BucketService,
    cartService: CartService,
    licenseListService,
    pricingService,
    $tnPopover: PopoverService
) {
    // show all license types by default
    angular.extend($scope, {
        appIsVisible: true,
        epubIsVisible: true,
    });

    let bucketsDirty = true;
    const vm = angular.extend(this, {
        buckets: {}, // used to populate keys in the select boxes
        calculateAppPrice: calculateAppPrice,
        calculateCpuPrice: calculateCpuPrice,
        calculateEpubPrice: calculateEpubPrice,
        calculateWebPrice: calculateWebPrice,
        calculatedPrices: {},
        disabledRights: {},
        fontRights: angular.copy(EMPTY_FONT_RIGHTS),
        initialize: initialize,
        licenses: [],
        selectLabels: {},
        setWebState: setWebState,
        toggleSelect: toggleSelect,
        updateLicenses: updateLicenses,
        removeLicenses: removeLicenses,
        closePopover,
        webState: 'hosted',
        templateUrl: 'tn.cart/edit-styles.html',
    });

    let _cart = {};

    function initialize(cart, licenses) {
        bucketsDirty = true;
        _cart = cart;
        vm.calculatedPrices = {
            web: {},
            web_self_hosted: {},
            cpu: {},
            ebook: {},
            app: {},
        };
        vm.licenses = licenses;
        _disableUpgradeAndRenew(licenses);
        _buildBuckets(licenses);
        _assignSelectValues(licenses);
    }

    function setWebState(state) {
        vm.webState = state;
        toggleSelect('web', true);
    }

    function _disableUpgradeAndRenew(licenses) {
        const disabledRights = {};
        Object.keys(licenses[0].fontRights).forEach((rightType) => {
            disabledRights[rightType] = licenses.some((license) => {
                return (
                    (hasOwnProperty(license.fontRights[rightType], 'renew') &&
                        license.fontRights[rightType].renew) ||
                    (hasOwnProperty(license.fontRights[rightType], 'upgrade') &&
                        license.fontRights[rightType].upgrade)
                );
            });
        });
        angular.copy(disabledRights, vm.disabledRights);
    }

    function calculateAppPrice(apps, years) {
        return pricingService
            .calculateLicensePrices(
                vm.licenses.map((license) => ({
                    fontRights: { app: { apps: apps, years: years } },
                    font: license.font,
                }))
            )
            .then((result) => result.reduce((total, p) => total + p.base, 0));
    }

    function calculateCpuPrice(computers) {
        return pricingService
            .calculateLicensePrices(
                vm.licenses.map((license) => ({
                    fontRights: { desktop: { computers: computers } },
                    font: license.font,
                }))
            )
            .then((result) => result.reduce((total, p) => total + p.base, 0));
    }

    function calculateEpubPrice(ebooks) {
        return pricingService
            .calculateLicensePrices(
                vm.licenses.map((license) => ({
                    fontRights: { epub: { ebooks: ebooks } },
                    font: license.font,
                }))
            )
            .then((result) => result.reduce((total, p) => total + p.base, 0));
    }

    function calculateWebPrice(views, years) {
        const selfHosted = vm.webState === 'self-hosted';

        return pricingService
            .calculateLicensePrices(
                vm.licenses.map((license) => ({
                    fontRights: {
                        web: {
                            views: views,
                            years: years,
                            self_hosted: selfHosted,
                        },
                    }, //eslint-disable-line camelcase
                    font: license.font,
                }))
            )
            .then((result) => result.reduce((total, p) => total + p.base, 0));
    }

    function toggleSelect(selectName, isActive) {
        //eslint-disable-line complexity
        vm.fontRights[selectName] = {};
        let webLabel;

        if (isActive) {
            const bucketTemplate = vm.buckets;
            switch (selectName) {
                case 'app':
                    vm.fontRights.app.apps = bucketTemplate.app[0].key;
                    vm.fontRights.app.years = bucketTemplate.year[0].key;
                    vm.selectLabels.appApps = bucketTemplate.app[0].key;
                    vm.selectLabels.appYears = bucketTemplate.year[0].label; /*+
							(bucketTemplate.year[0].key === 1 ? ' year' : ' years');*/
                    break;
                case 'desktop':
                    vm.fontRights.desktop.computers = bucketTemplate.cpu[0].key;
                    vm.selectLabels.desktop = bucketTemplate.cpu[0].label;
                    break;
                case 'epub':
                    vm.fontRights.epub.ebooks = bucketTemplate.ebook[0].key;
                    vm.selectLabels.epub = bucketTemplate.ebook[0].label;
                    break;
                case 'web':
                    webLabel = '';
                    vm.fontRights.web.years = bucketTemplate.year[0].key;
                    switch (vm.webState) {
                        case 'hosted':
                            webLabel = bucketTemplate.web[0].label;
                            vm.fontRights.web.views = bucketTemplate.web[0].key;
                            break;
                        case 'self-hosted':
                            webLabel = bucketTemplate.web_self_hosted[0].label;
                            vm.fontRights.web.views =
                                bucketTemplate.web_self_hosted[0].key;
                            vm.fontRights.web.self_hosted = true; //eslint-disable-line camelcase
                            break;
                    }
                    vm.selectLabels.webViews = webLabel;
                    vm.selectLabels.webYears = bucketTemplate.year[0].label;
            }
        } else {
            switch (selectName) {
                case 'app':
                    vm.fontRights.app.apps = null;
                    vm.fontRights.app.years = null;
                    break;
                case 'desktop':
                    vm.fontRights.desktop.computers = null;
                    break;
                case 'epub':
                    vm.fontRights.epub.ebooks = null;
                    break;
                case 'web':
                    vm.fontRights.web.years = null;
                    vm.fontRights.web.visits = null;
                    vm.fontRights.web.views = null;
            }
        }
    }

    function removeLicenses() {
        licenseListService.removeLicense(_cart.license, vm.licenses);

        if (_cart.id !== undefined && _cart.id !== null) {
            cartService.updateCartOnServer(_cart);
        }
    }

    function closePopover() {
        $tnPopover.hide();
    }

    function updateLicenses() {
        licenseListService.editLicenses(
            _cart.license,
            vm.licenses,
            angular.copy(vm.fontRights),
            licenseListService.getLicenseTypeArray(
                vm.disabledRights['app'] ? false : vm.isAppLicense,
                vm.disabledRights['desktop'] ? false : vm.isDesktopLicense,
                vm.disabledRights['epub'] ? false : vm.isEpubLicense,
                vm.disabledRights['web'] ? false : vm.isWebLicense
            ),
            true
        );

        if (_cart.id !== undefined && _cart.id !== null) {
            cartService.updateCartOnServer(_cart);
        }
    }

    function _assignSelectValues(licenses) {
        // Show rights that have values
        vm.isAppLicense = licenses.some(function (license) {
            return license.fontRights.app.apps;
        });
        vm.isDesktopLicense = licenses.some(function (license) {
            return license.fontRights.desktop.computers;
        });
        vm.isEpubLicense = licenses.some(function (license) {
            return license.fontRights.epub.ebooks;
        });
        const webLicenses = licenses.filter(
            (license) => license.fontRights.web.views
        );
        vm.isWebLicense = webLicenses.length > 0;
        vm.webStateMixed = false;
        // they're all in self-hosted mode? we're self-hosted, otherwise punt to hosted.
        if (vm.isWebLicense) {
            const selfHostedLicenses = webLicenses.filter(
                (license) => license.fontRights.web.self_hosted
            );

            if (selfHostedLicenses.length === webLicenses.length) {
                // they're all self-hosted
                vm.webState = 'self-hosted';
            } else {
                vm.webState = 'hosted';
                if (selfHostedLicenses.length > 0) {
                    vm.webStateMixed = true;
                }
            }
        }

        // If we have conflicting values choose the largest

        Object.keys(vm.fontRights).forEach(function (rightType) {
            Object.keys(vm.fontRights[rightType]).forEach(function (
                rightValue
            ) {
                if (
                    licenses.some(function (license) {
                        return (
                            license.fontRights[rightType][rightValue] ===
                            'unlimited'
                        );
                    })
                ) {
                    vm.fontRights[rightType][rightValue] = 'unlimited';
                } else {
                    const rightsValues = vm.licenses.map((license) => {
                        const value = license.fontRights[rightType][rightValue];
                        return value;
                    });
                    const max = Math.max(...rightsValues);
                    vm.fontRights[rightType][rightValue] = max > 0 ? max : null;
                }
            });
        });
    }

    function _buildBuckets(licenses) {
        // Build aggregated bucket
        let promise = $q.when(vm.buckets);
        if (bucketsDirty) {
            promise = bucketsService.getIntersectingBuckets(
                licenses.map((l) => l.font)
            );
            bucketsDirty = false;
        }
        return promise.then(function (intersectingBuckets) {
            const promises = [];
            // Assuming each bucket has the same options
            const calculatedPrices = vm.calculatedPrices;

            if (intersectingBuckets) {
                // get all the different kinds of licenses in there ...
                Object.keys(calculatedPrices).forEach((fontRightKey) =>
                    intersectingBuckets[fontRightKey].forEach((bucket, i) => {
                        intersectingBuckets[fontRightKey][i].label =
                            changeNumbersLabel(bucket.label);
                        switch (fontRightKey) {
                            case 'web_self_hosted':
                            case 'web':
                                intersectingBuckets['year'].forEach((term) =>
                                    promises.push(
                                        calculateWebPrice(
                                            bucket.key,
                                            term.key
                                        ).then((total) => {
                                            if (
                                                calculatedPrices[fontRightKey][
                                                    term.key
                                                ] === undefined
                                            ) {
                                                calculatedPrices[fontRightKey][
                                                    term.key
                                                ] = {};
                                            }
                                            calculatedPrices[fontRightKey][
                                                term.key
                                            ][bucket.key] = total;
                                        })
                                    )
                                );
                                break;
                            case 'cpu':
                                promises.push(
                                    calculateCpuPrice(bucket.key)
                                        // .then( total => calculatedPrices[key][bucket.key] = total )
                                        .then(
                                            (total) =>
                                                (calculatedPrices[fontRightKey][
                                                    bucket.key
                                                ] = total)
                                        )
                                );
                                break;
                            case 'ebook':
                                promises.push(
                                    calculateEpubPrice(bucket.key).then(
                                        (total) =>
                                            (calculatedPrices[fontRightKey][
                                                bucket.key
                                            ] = total)
                                    )
                                );
                                break;
                            case 'app':
                                intersectingBuckets['year'].forEach((term) =>
                                    promises.push(
                                        calculateAppPrice(
                                            bucket.key,
                                            term.key
                                        ).then((total) => {
                                            if (
                                                calculatedPrices[fontRightKey][
                                                    term.key
                                                ] === undefined
                                            ) {
                                                calculatedPrices[fontRightKey][
                                                    term.key
                                                ] = {};
                                            }
                                            calculatedPrices[fontRightKey][
                                                term.key
                                            ][bucket.key] = total;
                                        })
                                    )
                                );
                                break;
                        }
                    })
                );
            }

            vm.buckets = intersectingBuckets;
            vm.calculatedPrices = calculatedPrices;
            _setDefaultLabels(intersectingBuckets);
            return $q.all(promises);
        });
    }

    function changeNumbersLabel(label) {
        if (label.indexOf('-') !== -1 && label.indexOf(',') === -1) {
            const split = label.split('-');
            const newLabel1 = $filter('number')(split[0]),
                newLabel2 = $filter('number')(split[1]);
            return newLabel1 + '-' + newLabel2;
        }

        return label.charAt(0).toUpperCase() + label.slice(1);
    }

    function _setDefaultLabels(buckets) {
        // Set starting labels
        if (vm.isAppLicense) {
            vm.selectLabels['appApps'] = _getLabel(
                buckets.app,
                vm.fontRights.app.apps
            );
            vm.selectLabels['appYears'] = _getLabel(
                buckets.year,
                vm.fontRights.app.years
            );
        }
        if (vm.isDesktopLicense) {
            vm.selectLabels['desktop'] = _getLabel(
                buckets.cpu,
                vm.fontRights.desktop.computers
            );
        }
        if (vm.isEpubLicense) {
            vm.selectLabels['epub'] = _getLabel(
                buckets.ebook,
                vm.fontRights.epub.ebooks
            );
        }
        if (vm.isWebLicense) {
            if (vm.webState === 'self-hosted') {
                vm.selectLabels['webViews'] = _getLabel(
                    buckets.web_self_hosted,
                    vm.fontRights.web.views
                );
            } else {
                vm.selectLabels['webViews'] = _getLabel(
                    buckets.web,
                    vm.fontRights.web.views
                );
            }
            vm.selectLabels['webYears'] = _getLabel(
                buckets.year,
                vm.fontRights.web.years
            );
        }
    }

    function _getLabel(buckets, value) {
        return buckets.filter(function (item) {
            return item.key === value;
        })[0].label;
    }
}
EditStylesController.$inject = [
    '$scope',
    '$filter',
    '$q',
    'tnBuckets',
    'tn.cart.service',
    'tnLicenseListService',
    'tn.pricing.service',
    '$tnPopover',
];
